'use client';

import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useInternalPathname } from 'hooks/navigation/useInternalPathname';
import { usePrefersReducedMotion } from 'hooks/media/usePrefersReducedMotion';
import { useRegionLocale } from 'contexts/RegionLocaleContext';
import { BANNER_CAMPAIGNS, SUPPRESSED } from './banners/campaigns';
import { resolveBanners } from './banners/rules';
import { TILES } from './banners/tiles';
import type { BannerContext } from './banners/types';
import {
  applyMarketingBannerCssVars,
  getMarketingBannerHeightPx,
} from './marketingBannerVisibility';
import styles from './MarketingBanner.module.css';

export {
  isMarketingBannerActive,
  isMarketingBannerVisible,
  getMarketingBannerHeightPx,
  getHeaderStackHeightPx,
  MARKETING_BANNER_HEIGHT_PX,
  HEADER_BAR_HEIGHT_PX,
  MARKETING_BANNER_HEIGHT_VAR,
  HEADER_STACK_HEIGHT_VAR,
} from './marketingBannerVisibility';

const BANNER_TILE_HEIGHT_PX = 32;

// Track is [tile0, …, tileN-1, clone-of-tile0] and `position` only ever increases,
// so every leg including the wrap slides the same way (up).
// Missing pause control is a WCAG 2.2.2 gap: docs/a11y-maintenance-backlog.md#marketingbanner-rotation
const RotatingBanner = ({ tiles }: { tiles: React.ReactNode[] }) => {
  const items = tiles.filter(Boolean);
  const itemCount = items.length;
  const reducedMotion = usePrefersReducedMotion();
  const rotates = itemCount > 1 && !reducedMotion;

  const [position, setPosition] = useState(0);
  const [instant, setInstant] = useState(false);
  const [interacting, setInteracting] = useState(false);
  const [onScreen, setOnScreen] = useState(true);
  const [tabVisible, setTabVisible] = useState(true);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const trackRef = useRef<HTMLDivElement | null>(null);

  const paused = interacting || !onScreen || !tabVisible;
  const activeIndex = rotates ? position % itemCount : 0;
  const shiftIndex = rotates ? position : 0;

  useEffect(() => {
    if (!rotates || paused) return;
    const interval = setInterval(() => setPosition((p) => p + 1), 6000);
    return () => clearInterval(interval);
  }, [rotates, paused]);

  // The clone is pixel-identical to tile 0 at position 0, so snapping back with the
  // transition off is invisible and keeps the next leg sliding upward.
  useEffect(() => {
    if (!rotates || position !== itemCount) return;
    const el = trackRef.current;
    if (!el) return;
    const onEnd = (e: TransitionEvent) => {
      if (e.target !== el || e.propertyName !== 'transform') return;
      setInstant(true);
      setPosition(0);
    };
    el.addEventListener('transitionend', onEnd);
    return () => el.removeEventListener('transitionend', onEnd);
  }, [rotates, position, itemCount]);

  // Re-enable the transition on the frame after an instant reset.
  useEffect(() => {
    if (!instant) return;
    let raf2 = 0;
    const raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(() => setInstant(false));
    });
    return () => {
      cancelAnimationFrame(raf1);
      cancelAnimationFrame(raf2);
    };
  }, [instant]);

  useEffect(() => {
    if (!rotates) return;
    const el = containerRef.current;
    if (!el || typeof IntersectionObserver === 'undefined') return;
    const observer = new IntersectionObserver(
      ([entry]) => setOnScreen(entry.isIntersecting),
      { threshold: 0 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [rotates]);

  useEffect(() => {
    if (!rotates) return;
    const onChange = () =>
      setTabVisible(document.visibilityState === 'visible');
    onChange();
    document.addEventListener('visibilitychange', onChange);
    return () => document.removeEventListener('visibilitychange', onChange);
  }, [rotates]);

  // No clipping viewport on this path: content may grow past BANNER_TILE_HEIGHT_PX,
  // which is the pre-rotation behaviour and deliberate.
  if (!rotates) {
    return <div className={styles.rotatingBanner}>{items[0]}</div>;
  }

  const track = [...items, items[0]];

  return (
    <div
      className={styles.rotatingBanner}
      ref={containerRef}
      onMouseEnter={() => setInteracting(true)}
      onMouseLeave={() => setInteracting(false)}
    >
      <div className={styles.viewport}>
        <div
          ref={trackRef}
          className={styles.track}
          style={{
            transform: `translateY(-${shiftIndex * BANNER_TILE_HEIGHT_PX}px)`,
            transition: instant ? 'none' : undefined,
          }}
        >
          {track.map((tile, i) => {
            const isClone = rotates && i === itemCount;
            return (
              <div
                key={isClone ? 'clone' : i}
                className={styles.tile}
                aria-hidden={isClone ? true : i !== activeIndex}
                style={isClone ? { pointerEvents: 'none' } : undefined}
              >
                {tile}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

const useBannerContext = (): BannerContext => {
  const { region, serverNow } = useRegionLocale();
  const internalPathname = useInternalPathname();
  const path =
    internalPathname.length > 1
      ? internalPathname.replace(/\/$/, '')
      : internalPathname;
  return { region: (region || '').toUpperCase(), path, now: serverNow };
};

/**
 * Height (px) the marketing strip occupies on this route/region — 0 when no
 * campaign resolves. Layout that must sit below the strip reads this instead of
 * re-deriving the matrix, so a campaign change can never leave a hero offset by
 * a stale constant.
 */
export const useMarketingBannerOffset = (): number =>
  getMarketingBannerHeightPx(useBannerContext());

export const MarketingBanner = () => {
  const ctx = useBannerContext();

  const bannerHeightPx = getMarketingBannerHeightPx(ctx);

  // Only the CSS vars. `GlobalUI.headerHeight` is published by Header.tsx, which
  // is the one place that also knows the breakpoint and the scrolled-pill state
  // — two writers would fight over the same value.
  useLayoutEffect(() => {
    applyMarketingBannerCssVars(bannerHeightPx);
  }, [bannerHeightPx]);

  const campaigns = resolveBanners(ctx, BANNER_CAMPAIGNS, SUPPRESSED);
  if (!campaigns.length) return null;

  return (
    <RotatingBanner
      tiles={campaigns.map(({ id }) => {
        const Tile = TILES[id];
        return <Tile key={id} />;
      })}
    />
  );
};
